import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@a7y/tailwind-catalyst";
import type { AnnouncementDoc } from "@a7y/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { AnnouncementEditDialog } from "./AnnouncementEditDialog";
import { AnnouncementPreviewDialog } from "./AnnouncementPreviewDialog";
import { isHeldBulletin, isWeeklyBulletin } from "./constants";

const LIFECYCLE_BADGE_COLORS: Record<string, string> = {
  trialing: "sky",
  active: "green",
  canceled: "zinc",
  trial_expired: "amber",
  sandbox: "violet",
  past_due: "red",
  grace_overage: "orange",
};

export function AnnouncementListTab() {
  const queryClient = useQueryClient();
  const [editingAnnouncement, setEditingAnnouncement] =
    useState<AnnouncementDoc | null>(null);
  const [broadcastTargetId, setBroadcastTargetId] = useState<string | null>(
    null
  );
  const [previewTargetId, setPreviewTargetId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["announcements"],
    queryFn: () => api.getAnnouncements({ limit: 100 }),
  });

  const broadcastMutation = useMutation({
    mutationFn: (id: string) => api.broadcastAnnouncement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
      setBroadcastTargetId(null);
    },
  });

  const announcements = data?.data ?? [];
  const broadcastTarget = announcements.find(
    (a) => a.id === broadcastTargetId
  );
  // Read from the list, so a saved edit (which refetches it) re-renders the preview.
  const previewTarget = announcements.find((a) => a.id === previewTargetId);
  const previewDialog = previewTarget && (
    <AnnouncementPreviewDialog
      announcement={previewTarget}
      open
      onClose={() => setPreviewTargetId(null)}
    />
  );

  return (
    <>
      <Table dense striped>
        <TableHead>
          <TableRow>
            <TableHeader>Title</TableHeader>
            <TableHeader>Subject</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader>Broadcast</TableHeader>
            <TableHeader>Audience</TableHeader>
            <TableHeader>Created</TableHeader>
            <TableHeader className="text-right">Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center">
                <div className="mx-auto h-6 w-6 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-zinc-600" />
              </TableCell>
            </TableRow>
          ) : announcements.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="py-8 text-center">
                <p className="text-zinc-500">No announcements yet</p>
                <p className="mt-1 text-sm text-zinc-400">
                  Create one using the "Create New" tab.
                </p>
              </TableCell>
            </TableRow>
          ) : (
            announcements.map((ann) => (
              <TableRow key={ann.id}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <span>{ann.title}</span>
                    {isWeeklyBulletin(ann) && (
                      <Badge color="indigo">Weekly Bulletin</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="max-w-48 truncate text-zinc-600 dark:text-zinc-400">
                  {ann.subject}
                </TableCell>
                <TableCell>
                  {isHeldBulletin(ann) ? (
                    <Badge color="amber">Held</Badge>
                  ) : (
                    <Badge color={ann.active ? "green" : "zinc"}>
                      {ann.active ? "Active" : "Inactive"}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {ann.broadcastId ? (
                    <Badge color="blue">Sent</Badge>
                  ) : (
                    <span className="text-sm text-zinc-400">Not sent</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {ann.targetLifecycleStages.map((stage) => (
                      <Badge
                        key={stage}
                        color={
                          (LIFECYCLE_BADGE_COLORS[stage] ?? "zinc") as
                            | "sky"
                            | "green"
                            | "zinc"
                            | "amber"
                            | "violet"
                            | "red"
                            | "orange"
                        }
                      >
                        {stage.replace("_", " ")}
                      </Badge>
                    ))}
                    {ann.includeChurned && (
                      <Badge color="rose">churned</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-zinc-500">
                  {formatDate(ann.createdAt)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      plain
                      onClick={() => setEditingAnnouncement(ann)}
                    >
                      Edit
                    </Button>
                    <Button plain onClick={() => setPreviewTargetId(ann.id)}>
                      Preview
                    </Button>
                    {!ann.broadcastId && (
                      <Button
                        outline
                        onClick={() => setBroadcastTargetId(ann.id)}
                        disabled={!ann.active && !isHeldBulletin(ann)}
                      >
                        Broadcast
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Edit dialog */}
      {editingAnnouncement && (
        <AnnouncementEditDialog
          key={editingAnnouncement.id}
          announcement={editingAnnouncement}
          open={!!editingAnnouncement}
          onClose={() => setEditingAnnouncement(null)}
        />
      )}

      {/* Email preview from a row. Opened from the Broadcast confirmation it
          renders inside that dialog, so Headless UI stacks it as a child. */}
      {!broadcastTargetId && previewDialog}

      {/* Broadcast confirmation dialog */}
      <Dialog
        open={!!broadcastTargetId}
        onClose={() => setBroadcastTargetId(null)}
        size="sm"
      >
        {broadcastTargetId && previewDialog}
        <DialogTitle>Confirm Broadcast</DialogTitle>
        <DialogBody>
          <div className="space-y-4">
            <div className="rounded-lg bg-amber-50 p-4 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              This will send the announcement email to all matching recipients.
              This action cannot be undone.
            </div>

            {broadcastTarget && (
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-zinc-500 dark:text-zinc-400">Title</dt>
                  <dd className="font-medium text-zinc-900 dark:text-white">
                    {broadcastTarget.title}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-zinc-500 dark:text-zinc-400">Subject</dt>
                  <dd className="font-medium text-zinc-900 dark:text-white">
                    {broadcastTarget.subject}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-zinc-500 dark:text-zinc-400">
                    Audience
                  </dt>
                  <dd className="font-medium text-zinc-900 dark:text-white">
                    {broadcastTarget.targetLifecycleStages.join(", ")}
                    {broadcastTarget.includeChurned ? ", churned" : ""}
                  </dd>
                </div>
              </dl>
            )}
          </div>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setBroadcastTargetId(null)}>
            Cancel
          </Button>
          <Button
            outline
            onClick={() => setPreviewTargetId(broadcastTargetId)}
          >
            Preview email
          </Button>
          <Button
            color="red"
            disabled={broadcastMutation.isPending}
            onClick={() => {
              if (broadcastTargetId) {
                broadcastMutation.mutate(broadcastTargetId);
              }
            }}
          >
            {broadcastMutation.isPending
              ? "Broadcasting..."
              : "Confirm Broadcast"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Broadcast result feedback */}
      {broadcastMutation.isSuccess && (
        <div className="mt-4 rounded-lg bg-green-50 p-3 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-400">
          Broadcast initiated successfully.
        </div>
      )}
      {broadcastMutation.isError && (
        <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
          {broadcastMutation.error?.message || "Failed to broadcast announcement"}
        </div>
      )}
    </>
  );
}
